import React, { useMemo } from 'react';
import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteProps, AutocompleteValue } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface NewNullableAutocompleteProps {
  options?: { label: string, value: any }[];
  onChange?: (event: React.SyntheticEvent<Element, Event>, value: any, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<any> | undefined) => void;
}

export default function NullableAutocomplete<Value, Multiple extends boolean | undefined = false, DisableClearable extends boolean | undefined = false, FreeSolo extends boolean | undefined = false, ChipComponent extends React.ElementType<any, keyof React.JSX.IntrinsicElements> = "div">(props: NewNullableAutocompleteProps & AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>): JSX.Element {
  const { i18n } = useTranslation();

  function convertValue(value: any) : AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | undefined {
    let newValue = value;
    if (props.multiple && Array.isArray(props.value)) {
      newValue = props.value.map((value) => {
        return convertValue(value) as AutocompleteValue<any, Multiple, DisableClearable, FreeSolo>;
      });
    } else if (props.value === null) {
      newValue = "__null__" as AutocompleteValue<any, Multiple, DisableClearable, FreeSolo>;
    } else if (props.value === undefined) {
      newValue = "__undefined__" as AutocompleteValue<any, Multiple, DisableClearable, FreeSolo>;
    } else if (typeof props.value === 'object') {
      if (props.value?.id === null) {
        newValue = { ...props.value, id: "__null__" };
      } else if (props.value?.id === undefined) {
        newValue = { ...props.value, id: "__undefined__" };
      }
    }
    return newValue as AutocompleteValue<any, Multiple, DisableClearable, FreeSolo>;
  }

  const convertedValue:AutocompleteValue<any, Multiple, DisableClearable, FreeSolo> | undefined = useMemo(() => {
    return convertValue(props.value);
  }, [props.value]);

  function convertValueBack(value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>) : AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | null | undefined | any[] {
    let newValue: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | null | undefined | any[] = value;
    if (value === "__null__") {
      newValue = null;
    } else if (value === "__undefined__") {
      newValue = undefined;
    } else if (Array.isArray(value)) {
      newValue = value.map((val) => {
        if (val === "__null__") {
          return null;
        } else if (val === "__undefined__") {
          return undefined;
        }
        return val;
      });
    } else if (typeof value === 'object') {
      if (value?.id === "__null__") {
        newValue = { ...value, id: null };
      } else if (value?.id === "__undefined__") {
        newValue = { ...value, id: undefined };
      }
    }
    return newValue;
  }

  function onChange(event: React.SyntheticEvent<Element, Event>, value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<any> | undefined) : void {
    if (props.onChange) {
      return props.onChange(event, convertValueBack(value), reason, details);
    }
  }

  const activeOptions:any[] = useMemo(() => {
    const options = (props.options || []).map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          id: option
        };
      } else if (option === null) {
        return {
          label: i18n.t("null"),
          id: "__null__"
        };
      } else if (option === undefined) {
        return {
          label: i18n.t("undefined"),
          id: "__undefined__"
        };
      }
      let id = option.id;
      if (id === null) {
        id = "__null__";
      } else if (id === undefined) {
        id = "__undefined__";
      }
      return {
        ...option,
        id: id
      };
    });
    return options;
  }, [props.options]);

  const activeProps = useMemo(() => {
    return {
      ...props,
      value: convertedValue,
      onChange: onChange,
      options: activeOptions
    };
  }, [props, convertedValue, activeOptions]);

return (
  <Autocomplete
    {...activeProps}
  />
);
}
